import React, { useTransition, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { TextField, Box, Typography } from "@mui/material";
import Skeleton from "@mui/lab/Skeleton";
import { graphql } from "react-relay";
import { useFragment } from "react-relay/hooks";
import { UserRole } from "../../constants";
import { useQueryParams } from "../../hooks/useQueryParams";
import { UsersTableRow } from "./UsersTableRow";
import { UsersTable_users$key } from "./__generated__/UsersTable_users.graphql";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ADMIN_USERS_ROUTE } from "../../routes/routes";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
  },

  tabs: {
    "& span": {
      fontWeight: "bold",
      fontSize: 14,
      textTransform: "uppercase",
    },
    borderBottom: "1px solid",
    borderBottomColor: theme.palette.selected.main,
  },

  container: {
    margin: theme.spacing(3, 2),

    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(3, 6),
    },
  },

  searchInput: {
    "& input": {
      fontSize: 15,
      padding: theme.spacing(2),
      minWidth: theme.spacing(50),
    },
    marginBottom: theme.spacing(2),
  },

  summary: {
    margin: theme.spacing(1.5, 0),
    fontWeight: 500,
    fontSize: 16,
  },

  table: {
    minWidth: 650,

    "& th": {
      fontWeight: "bold",
      fontSize: 15,
    },

    "& td": {
      fontWeight: 500,
      fontSize: 14,
    },

    "& tfoot td": {
      borderBottom: "none",
    },
  },
}));

const fragment = graphql`
  fragment UsersTable_users on UserConnection {
    totalCount
    edges {
      node {
        id
        ...UsersTableRow_user
      }
    }
  }
`;

export interface UsersTableProps {
  usersRef: UsersTable_users$key;
}

export function UsersTable(props: UsersTableProps) {
  const navigate = useNavigate();
  const { usersRef } = props;
  const s = useStyles();
  const [isPending, startTransition] = useTransition();
  const [queryParams, setQueryParams] = useQueryParams({
    page: "0",
    perPage: "5",
    search: null,
    role: null,
  });
  const page = parseInt(queryParams.page) || 0;
  const rowsPerPage = parseInt(queryParams.perPage) || 5;
  const users = useFragment(fragment, usersRef);
  const [searchParams] = useSearchParams();

  const roleFromParam = searchParams.get("role");
  const selectedRole = roleFromParam ?? UserRole.COACH;

  const querySearch = queryParams.search || "";
  const [search, setSearch] = useState(querySearch);

  useEffect(() => {
    if (querySearch !== search) {
      const timer = setTimeout(() => {
        startTransition(() => {
          setQueryParams({
            search,
            page: "0",
          });
        });
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [querySearch, search, setQueryParams]);

  const handleChangePage = (event, newPage) => {
    startTransition(() => {
      setQueryParams({
        page: newPage.toString(),
      });
    });
  };

  const handleChangeRowsPerPage = (event) => {
    startTransition(() => {
      setQueryParams({
        perPage: event.target.value,
        page: "0",
      });
    });
  };

  const handleTabChange = (event, value) => {
    startTransition(() => {
      setSearch("");
      setQueryParams({
        role: value,
        page: "0",
        search: null,
      });
      navigate(ADMIN_USERS_ROUTE + `?page=0&role=${value}`);
    });
  };
  const emptyRows = Array.from(new Array(rowsPerPage));
  return (
    <Paper className={s.root}>
      <Tabs
        className={s.tabs}
        value={selectedRole}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleTabChange}
      >
        <Tab label="Coaches" value={UserRole.COACH} />
        <Tab label="Clients" value={UserRole.CLIENT} />
      </Tabs>

      <Box className={s.container}>
        <TextField
          className={s.searchInput}
          variant="outlined"
          placeholder="Search Records"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Typography className={s.summary}>
          {users.totalCount} record{users.totalCount === 1 ? "" : "s"} found
        </Typography>

        {isPending ? (
          <Table className={s.table} aria-label="skeleton table">
            <TableHead>
              <TableRow>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {emptyRows.map((_, index) => (
                <TableRow sx={{ height: 80 }} key={index}>
                  {Array.from({ length: 5 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex} align="center">
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table className={s.table} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {selectedRole === UserRole.COACH && (
                  <>
                    <TableCell align="center">No. Clients</TableCell>
                    <TableCell align="center">No. Programs</TableCell>
                  </>
                )}
                <TableCell align="center">Last Login</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.edges.map(({ node }) => (
                <UsersTableRow userRef={node} key={node.id} />
              ))}

              {!users.edges.length && (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={selectedRole === UserRole.COACH ? 6 : 4}
                  >
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={6}
                  count={users.totalCount}
                  rowsPerPage={rowsPerPage}
                  page={Number(page)}
                  showFirstButton
                  showLastButton
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </Box>
    </Paper>
  );
}
